// ── BCHNostr — BCH Video home ─────────────────────────────────────
//
// A YouTube-style home for video that BCHNostr does not host. Every rail is
// built from signals a viewer can inspect:
//
//   Recommended    follows → interests → recency → engagement (transparent)
//   Latest         straight recency
//   Trending       unique engagement with decay, capped per creator
//   Following      creators the viewer follows on Nostr
//   BCH            the Bitcoin Cash category
//   New Creators   first-time publishers, one video each
//   Popular        all-time engagement, no recency decay
//
// Promoted videos sit in their own labelled row ABOVE the organic feed and
// are removed from it, so a paid slot is never dressed up as a
// recommendation.
//
// Performance: no player is mounted anywhere on this page. Cards are
// images. The grid pages in via IntersectionObserver rather than rendering
// the whole catalogue.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Video, Search, X, Sparkles, Clock, Flame, Users, Coins, Plus,
  RefreshCw, UserPlus, History, ListVideo, ChevronRight, Trash2, Info,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import {
  useVideoDirectory, useRecommendations, useVideoBoosts, useAllPlaylists,
  useWatchHistoryState,
} from '../hooks/useVideo';
import VideoCard, { VideoCardSkeleton } from '../components/VideoCard';
import AddVideoModal from '../components/AddVideoModal';
import {
  VIDEO_CATEGORIES, videoCategoryLabel, videoWatchPath, videoChannelPath,
  playlistPath, videoCoordinate, videoViewCount,
  clearWatchHistory, removeFromHistory, formatCount,
  type BCHVideo, type VideoEngagement, type VideoBoostData, type ScoredVideo,
} from '../lib/video';
import { BCHLogo } from '../components/Layout';

type RailId =
  | 'recommended' | 'latest' | 'trending' | 'following'
  | 'bch' | 'new-creators' | 'popular';

const RAILS: Array<{ id: RailId; label: string; icon: React.ElementType }> = [
  { id: 'recommended',  label: 'Recommended',  icon: Sparkles },
  { id: 'latest',       label: 'Latest',       icon: Clock },
  { id: 'trending',     label: 'Trending',     icon: Flame },
  { id: 'following',    label: 'Following',    icon: Users },
  { id: 'bch',          label: 'BCH',          icon: Coins },
  { id: 'new-creators', label: 'New Creators', icon: UserPlus },
  { id: 'popular',      label: 'Popular',      icon: Flame },
];

/** How many cards render per page of the infinite grid. */
const PAGE_SIZE = 12;

export default function VideosPage() {
  const { user } = useAppContext();
  const [rail, setRail] = useState<RailId>('recommended');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showWhy, setShowWhy] = useState(false);

  const { videos, loading, refetch } = useVideoDirectory(category);
  const {
    recommended, trending, following, newCreators, popular, engagement,
  } = useRecommendations(videos);
  const { byVideo: boostsByVideo } = useVideoBoosts();
  const { playlists } = useAllPlaylists();
  const { entries: history, refresh: refreshHistory } = useWatchHistoryState();

  // ── Search across titles, tags, categories and creators ──
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return videos.filter(v =>
      v.title.toLowerCase().includes(q) ||
      v.description.toLowerCase().includes(q) ||
      v.hashtags.some(t => t.includes(q)) ||
      videoCategoryLabel(v.category).toLowerCase().includes(q),
    );
  }, [videos, query]);

  /** The organic list for the selected rail. */
  const railVideos = useMemo<BCHVideo[]>(() => {
    switch (rail) {
      case 'latest':       return videos;
      case 'trending':     return trending;
      case 'following':    return following;
      case 'bch':          return videos.filter(v => v.category === 'bch');
      case 'new-creators': return newCreators;
      case 'popular':      return popular;
      case 'recommended':
      default:             return recommended.map(r => r.video);
    }
  }, [rail, videos, trending, following, newCreators, popular, recommended]);

  /** Recommendation reasons, keyed by coordinate, for the Recommended rail. */
  const reasonsByCoord = useMemo(() => {
    const map = new Map<string, string[]>();
    if (rail !== 'recommended') return map;
    for (const r of recommended) {
      map.set(videoCoordinate(r.video.pubkey, r.video.id), r.reasons);
    }
    return map;
  }, [rail, recommended]);

  /**
   * Promoted videos, shown in their own labelled row and REMOVED from the
   * organic list below. A paid slot must never masquerade as an organic
   * recommendation, and an explicit search or a specific rail choice is the
   * viewer's intent — we don't hijack it.
   */
  const promoted = useMemo<Array<{ video: BCHVideo; boost: VideoBoostData }>>(() => {
    if (query.trim()) return [];
    if (rail !== 'recommended' && rail !== 'latest') return [];
    const out: Array<{ video: BCHVideo; boost: VideoBoostData }> = [];
    for (const video of videos) {
      const boost = boostsByVideo.get(`${video.pubkey}:${video.id}`);
      if (boost) out.push({ video, boost });
    }
    return out.sort((a, b) => b.boost.priceSats - a.boost.priceSats).slice(0, 3);
  }, [videos, boostsByVideo, rail, query]);

  const promotedKeys = useMemo(
    () => new Set(promoted.map(p => `${p.video.pubkey}:${p.video.id}`)),
    [promoted],
  );

  const list = useMemo(() => {
    const base = searched ?? railVideos;
    if (promotedKeys.size === 0) return base;
    return base.filter(v => !promotedKeys.has(`${v.pubkey}:${v.id}`));
  }, [searched, railVideos, promotedKeys]);

  // Reset paging whenever the view changes.
  useEffect(() => { setVisible(PAGE_SIZE); }, [rail, category, query]);

  // ── Infinite scroll ──
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || visible >= list.length) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setVisible(v => Math.min(v + PAGE_SIZE, list.length));
      }
    }, { rootMargin: '600px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, list.length]);

  const handleClearHistory = useCallback(() => {
    clearWatchHistory();
    refreshHistory();
  }, [refreshHistory]);

  const shown = list.slice(0, visible);

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" /> BCH Video
          </h1>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
            >
              <Plus className="w-3.5 h-3.5" /> Add Video
            </button>
            <button
              onClick={refetch}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-primary"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search videos, creators, tags…"
              className="w-full pl-9 pr-9 py-2 rounded-full bg-muted text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Rails */}
        {!query && (
          <div className="flex gap-1.5 px-4 pb-2 overflow-x-auto scrollbar-hide">
            {RAILS.map(r => (
              <button
                key={r.id}
                onClick={() => setRail(r.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  rail === r.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                <r.icon className="w-3 h-3" /> {r.label}
              </button>
            ))}
          </div>
        )}

        {/* Categories */}
        <div className="flex gap-1.5 px-4 pb-2.5 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setCategory('all')}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border transition-colors ${
              category === 'all'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          {VIDEO_CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border transition-colors ${
                category === c.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Continue watching ── */}
      {!query && history.length > 0 && (
        <ContinueWatching
          history={history}
          onClear={handleClearHistory}
          onRemove={(pubkey, dTag) => { removeFromHistory(pubkey, dTag); refreshHistory(); }}
        />
      )}

      {/* ── Playlists ── */}
      {!query && playlists.length > 0 && (
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-xs font-bold flex items-center gap-1.5 mb-2.5">
            <ListVideo className="w-3.5 h-3.5 text-primary" /> Playlists
          </h2>
          <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-1">
            {playlists.slice(0, 12).map(pl => (
              <Link
                key={`${pl.pubkey}:${pl.id}`}
                to={playlistPath(pl.pubkey, pl.id)}
                className="shrink-0 w-40 group"
              >
                <div className="relative aspect-video rounded-lg overflow-hidden bg-muted border border-border group-hover:border-primary/50 transition-colors">
                  {pl.image ? (
                    <img src={pl.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-emerald-500/5">
                      <ListVideo className="w-6 h-6 text-primary/40" />
                    </div>
                  )}
                  <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/80 text-white text-[9px] font-semibold">
                    {pl.videoRefs.length} videos
                  </span>
                </div>
                <p className="text-[11px] font-semibold mt-1 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                  {pl.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── Promoted ── */}
      {promoted.length > 0 && (
        <div className="px-4 pt-4 pb-1">
          <div className="flex items-center gap-2 mb-2.5">
            <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              Promoted
            </h2>
            <p className="text-[10px] text-muted-foreground/70">
              Paid placement, not a recommendation
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
            {promoted.map(({ video, boost }) => (
              <VideoCard
                key={`promo-${video.pubkey}:${video.id}`}
                video={video}
                boost={boost}
                engagement={engagement.get(videoCoordinate(video.pubkey, video.id))}
                localViews={videoViewCount(video.pubkey, video.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Rail heading + transparency ── */}
      {!query && (
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <h2 className="text-sm font-bold flex-1">
            {RAILS.find(r => r.id === rail)?.label}
            {category !== 'all' && (
              <span className="text-muted-foreground font-normal">
                {' '}· {videoCategoryLabel(category)}
              </span>
            )}
          </h2>
          {(rail === 'recommended' || rail === 'trending') && (
            <button
              onClick={() => setShowWhy(v => !v)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary"
            >
              <Info className="w-3 h-3" /> How this is ranked
            </button>
          )}
        </div>
      )}

      {showWhy && !query && (rail === 'recommended' || rail === 'trending') && (
        <RankingExplainer rail={rail} />
      )}

      {/* ── Body ── */}
      <div className="px-4 pt-1">
        {loading && videos.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
            {Array.from({ length: 6 }).map((_, i) => <VideoCardSkeleton key={i} />)}
          </div>
        ) : shown.length === 0 ? (
          <EmptyRail
            rail={rail}
            query={query}
            loggedIn={!!user}
            onAdd={() => setAddOpen(true)}
            onBrowseLatest={() => { setRail('latest'); setCategory('all'); }}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
              {shown.map(video => (
                <VideoCard
                  key={`${video.pubkey}:${video.id}`}
                  video={video}
                  engagement={engagement.get(videoCoordinate(video.pubkey, video.id))}
                  boost={boostsByVideo.get(`${video.pubkey}:${video.id}`)}
                  localViews={videoViewCount(video.pubkey, video.id)}
                  reasons={reasonsByCoord.get(videoCoordinate(video.pubkey, video.id))}
                />
              ))}
            </div>
            {visible < list.length && (
              <div ref={sentinelRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5 mt-5">
                {Array.from({ length: 3 }).map((_, i) => <VideoCardSkeleton key={i} />)}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Honest footer ── */}
      {videos.length > 0 && (
        <p className="px-4 pt-6 text-[10px] text-muted-foreground/70 leading-relaxed">
          BCHNostr doesn't host video. Every video here plays from its own provider —
          YouTube, Odysee, PeerTube, or a direct media URL — while the discussion, tips
          and boosts live on Nostr and Bitcoin Cash.
        </p>
      )}

      {addOpen && <AddVideoModal onClose={() => setAddOpen(false)} onPublished={refetch} />}
    </div>
  );
}

// ── Continue watching ─────────────────────────────────────────────
//
// Read from a device-local list. No server copy, no watch positions, no
// personal data beyond "you opened this" — and one button erases it.

function ContinueWatching({
  history, onClear, onRemove,
}: {
  history: ReturnType<typeof useWatchHistoryState>['entries'];
  onClear: () => void;
  onRemove: (pubkey: string, dTag: string) => void;
}) {
  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="flex items-center gap-2 mb-2.5">
        <h2 className="text-xs font-bold flex items-center gap-1.5 flex-1">
          <History className="w-3.5 h-3.5 text-primary" /> Continue watching
        </h2>
        <button
          onClick={onClear}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-red-500"
          title="Clear your watch history from this browser"
        >
          <Trash2 className="w-3 h-3" /> Clear
        </button>
      </div>
      <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-1">
        {history.slice(0, 12).map(entry => (
          <div key={`${entry.pubkey}:${entry.dTag}`} className="shrink-0 w-40 group relative">
            <Link to={videoWatchPath(entry.pubkey, entry.dTag)}>
              <div className="aspect-video rounded-lg overflow-hidden bg-muted border border-border group-hover:border-primary/50 transition-colors">
                {entry.thumbnailUrl ? (
                  <img src={entry.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Video className="w-5 h-5 text-primary/40" />
                  </div>
                )}
              </div>
              <p className="text-[11px] font-medium mt-1 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                {entry.title || 'Video'}
              </p>
            </Link>
            <button
              onClick={() => onRemove(entry.pubkey, entry.dTag)}
              className="absolute top-1 right-1 p-1 rounded-md bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
              title="Remove from history"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground/70 mt-1.5">
        Stored in this browser only. Never uploaded.
      </p>
    </div>
  );
}

// ── Ranking explainer ─────────────────────────────────────────────
//
// The ranking is a small, stated formula, so we state it. A viewer who
// wants to know why a video is in front of them is entitled to an answer.

function RankingExplainer({ rail }: { rail: 'recommended' | 'trending' }) {
  return (
    <div className="mx-4 mb-3 rounded-xl border border-border bg-muted/40 p-3 space-y-1.5">
      {rail === 'recommended' ? (
        <>
          <p className="text-[11px] font-semibold">How Recommended is ranked</p>
          <ol className="text-[10px] text-muted-foreground leading-relaxed list-decimal pl-4 space-y-0.5">
            <li>Creators you follow on Nostr</li>
            <li>Categories you've opened before (counted in this browser only)</li>
            <li>How recently the video was published</li>
            <li>Engagement — how many <em>different</em> people tipped, replied or reacted</li>
            <li>How widely the community follows the creator</li>
          </ol>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Videos you've already opened are pushed down, and no creator can take more
            than three slots. There's no machine-learning model and nothing about you
            leaves your device.
          </p>
        </>
      ) : (
        <>
          <p className="text-[11px] font-semibold">How Trending is ranked</p>
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            By how many <strong className="text-foreground">different people</strong>
            {' '}engaged — tips, replies and reactions — in the last two weeks, with newer
            activity weighted higher. Deliberately <strong className="text-foreground">not</strong>
            {' '}by raw view count: BCHNostr can't verify plays that happen on someone else's
            platform, and a raw count rewards whoever can generate the most requests.
            No creator may hold more than two slots.
          </p>
        </>
      )}
    </div>
  );
}

// ── Empty states ──────────────────────────────────────────────────

function EmptyRail({
  rail, query, loggedIn, onAdd, onBrowseLatest,
}: {
  rail: RailId;
  query: string;
  loggedIn: boolean;
  onAdd: () => void;
  onBrowseLatest: () => void;
}) {
  if (query) {
    return (
      <div className="text-center py-16">
        <Search className="w-10 h-10 mx-auto text-muted-foreground/25 mb-3" />
        <p className="text-sm text-muted-foreground">No videos match “{query}”.</p>
      </div>
    );
  }

  const copy: Record<RailId, { title: string; body: string }> = {
    recommended: {
      title: 'Nothing to recommend yet',
      body: 'Follow some creators and watch a few videos — recommendations build from your own follows and clicks.',
    },
    latest: {
      title: 'No videos yet',
      body: 'Be the first. Paste a link to a video you already host anywhere online.',
    },
    trending: {
      title: 'Nothing trending yet',
      body: 'Trending needs real engagement — tips, replies and reactions from different people.',
    },
    following: {
      title: loggedIn ? 'No videos from people you follow' : 'Log in to see your follows',
      body: loggedIn
        ? 'The creators you follow haven’t added videos yet.'
        : 'Your Nostr follow list becomes your video subscriptions — no separate account.',
    },
    bch: {
      title: 'No Bitcoin Cash videos yet',
      body: 'Add one and it lands here immediately.',
    },
    'new-creators': {
      title: 'No new creators yet',
      body: 'This rail highlights people publishing their first videos.',
    },
    popular: {
      title: 'Nothing popular yet',
      body: 'Popularity is measured from tips, replies and reactions — not view counts.',
    },
  };

  const { title, body } = copy[rail];

  return (
    <div className="text-center py-16 px-6">
      <Video className="w-12 h-12 mx-auto text-muted-foreground/25 mb-3" />
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">{body}</p>
      <div className="flex items-center justify-center gap-2 mt-4">
        <button
          onClick={onAdd}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
        >
          <Plus className="w-3.5 h-3.5" /> Add a video
        </button>
        {rail !== 'latest' && (
          <button
            onClick={onBrowseLatest}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-muted text-xs font-semibold hover:bg-muted/70"
          >
            Browse latest <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Shared empty state (exported for the other video pages) ───────

export function VideoEmptyState({ title, body, action }: {
  title: string;
  body: string;
  action?: { to: string; label: string };
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-6 text-center">
      <Video className="w-14 h-14 text-muted-foreground/25" />
      <h2 className="text-lg font-bold">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">{body}</p>
      {action && (
        <Link
          to={action.to}
          className="mt-1 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}

// ── Tip / engagement summary chip (shared) ────────────────────────

export function EngagementChips({ engagement }: { engagement: VideoEngagement }) {
  if (engagement.uniqueTippers === 0 && engagement.comments === 0 && engagement.reactions === 0) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2.5 text-[11px] text-muted-foreground">
      {engagement.uniqueTippers > 0 && (
        <span className="flex items-center gap-1 text-primary font-medium">
          <BCHLogo size={11} />
          {engagement.uniqueTippers} tipper{engagement.uniqueTippers === 1 ? '' : 's'}
        </span>
      )}
      {engagement.comments > 0 && <span>{formatCount(engagement.comments)} comments</span>}
      {engagement.reactions > 0 && <span>{formatCount(engagement.reactions)} reactions</span>}
    </div>
  );
}

/** Re-exported so the channel page can share the grid layout constants. */
export { PAGE_SIZE as VIDEO_PAGE_SIZE };
export type { ScoredVideo, RailId };
export { videoChannelPath };
